import React, { useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CloseIcon from '@mui/icons-material/Close';
import { captureChartInteraction } from '../analytics/chartsTool';
import { TEXT_MSGS } from '../common/utility/constant';

const EMPTY_DATA_MESSAGES = new Set([
  TEXT_MSGS.CHART_DATA_NOT_AVAILABLE,
  TEXT_MSGS.MP_NO_DATA,
]);

/**
 * AppErrorPopup
 *
 * Centralized popup for backend-driven errors (replacement for snackbar flows).
 * - `open`: controls visibility
 * - `message`: string (backend message or fallback)
 * - `severity`: "error" | "warning" | "info" | "success" (drives icon/color)
 * - `onClose`: close handler
 * - Optional:
 *    - `title`: headline text (defaults by severity)
 *    - `primaryAction`: { label, onClick }
 *    - `secondaryAction`: { label, onClick }
 *    - `showCloseX`: boolean to show corner close button
 *    - `width`, `maxWidth`, `topOffset`: layout tweaks if needed
 *    - `autoCloseTimer`: number in milliseconds for auto-close (default: 3000ms / 3 seconds). Set to 0 or null to disable.
 */
export default function AppErrorPopup({
  open,
  message,
  severity = 'error',
  onClose,
  title,
  primaryAction,
  secondaryAction,
  showCloseX = false,
  width = { xs: '90%', sm: '480px' },
  maxWidth = '520px',
  topOffset = '50%',
  autoCloseTimer = 3000
}) {
  
  const severityConfig = {
    error: {
      icon: <ErrorOutlineRoundedIcon sx={{ color: '#e53935' }} />,
      color: '#e53935',
      defaultTitle: 'Something went wrong'
    },
    warning: {
      icon: <WarningAmberRoundedIcon sx={{ color: '#f9a825' }} />,
      color: '#f9a825',
      defaultTitle: 'Please check and try again'
    },
    info: {
      icon: <InfoOutlinedIcon sx={{ color: '#3f51b5' }} />,
      color: '#3f51b5',
      defaultTitle: 'Notification'
    },
    success: {
      icon: <CheckCircleOutlineRoundedIcon sx={{ color: '#2e7d32' }} />,
      color: '#2e7d32',
      defaultTitle: 'Success'
    }
  };

  const cfg = severityConfig[severity] || severityConfig.error;
  const headline = title || cfg.defaultTitle;
  const primary = primaryAction;
  const secondary = secondaryAction || {
    label: 'Close',
    onClick: onClose
  };

  const analyticsCapturedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      analyticsCapturedRef.current = false;
      return;
    }
    if (analyticsCapturedRef.current) {
      return;
    }
    analyticsCapturedRef.current = true;
    if (severity === 'error') {
      const isNetwork = message === TEXT_MSGS.NETWORK_ERROR_MSG;
      const isOffline = message === TEXT_MSGS.INTERNET_ERROR_MESSAGE;
      captureChartInteraction('charts_error_seen', {
        load_time_ms: 0,
        error_category: isOffline ? 'offline' : isNetwork ? 'network' : 'displayed',
        error_code: isOffline ? 'offline' : isNetwork ? 'network' : 'error',
      });
      if (isNetwork || isOffline) {
        captureChartInteraction('charts_data_load_failed', {
          load_time_ms: 0,
          error_category: isOffline ? 'offline' : 'network',
          error_code: isOffline ? 'offline' : 'network',
        });
      }
      return;
    }
    if (EMPTY_DATA_MESSAGES.has(message)) {
      captureChartInteraction('charts_empty_state_seen', {
        load_time_ms: 0,
        error_category: 'empty',
        error_code: 'no_data',
      });
    }
  }, [open, severity, message]);

  // Auto-close timer effect
  useEffect(() => {
    // Only set timer if popup is open and autoCloseTimer is provided and > 0
    if (open && autoCloseTimer && autoCloseTimer > 0) {
      const timer = setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, autoCloseTimer);

      // Cleanup timer on unmount or when open/autoCloseTimer changes
      return () => {
        clearTimeout(timer);
      };
    }
  }, [open, autoCloseTimer, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="app-error-popup-title"
      aria-describedby="app-error-popup-message"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(30, 60, 114, 0.6)',
          backdropFilter: 'blur(8px)'
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: topOffset,
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width,
          maxWidth,
        bgcolor: '#ffffff',
          borderRadius: '20px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
          p: 3,
          outline: 'none'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            mb: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {cfg.icon}
            <Typography
              id="app-error-popup-title"
              variant="h6"
              sx={{ fontWeight: 700, color: '#2b2c40' }}
            >
              {headline}
            </Typography>
          </Box>
          {showCloseX && (
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="close"
              sx={{ position: 'absolute', top: 0, right: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            background: 'linear-gradient(135deg, #f7f9ff, #ecf1ff)',
            borderLeft: `4px solid ${cfg.color}`,
            borderRadius: '10px',
            p: 2,
            mb: 3
          }}
        >
          <Typography id="app-error-popup-message" sx={{ color: '#555', lineHeight: 1.6, fontSize: '14px' }}>
            {message}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            justifyContent: 'center'
          }}
        >
          {primary && (
            <Button
              variant="contained"
              onClick={primary.onClick}
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: '#fff',
                border: 'none',
                borderRadius: '25px',
                textTransform: 'none',
                fontWeight: 700,
                letterSpacing: 0.2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8, #6a4190)',
                  boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)'
                }
              }}
            >
              {primary.label}
            </Button>
          )}
          {secondary && (
            <Button
              variant="outlined"
              onClick={secondary.onClick}
              fullWidth
              sx={{
                borderRadius: '25px',
                textTransform: 'none',
                fontWeight: 700,
                letterSpacing: 0.2,
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': {
                  borderColor: '#667eea',
                  backgroundColor: 'rgba(102, 126, 234, 0.08)'
                }
              }}
            >
              {secondary.label}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
